// ATTENTION, THIS CODE IS TEMPORARY AS BROWSE PAGE HAS AB TEST.
// THIS IS NECESSARY FOR METADATA/SEO AND DATALAYER, BUT AFTER WE REMOVE THE AB TEST, ALL THE CODE REFERENT TO ALGOLIA MUST BE DELETED
import { getABTestValues } from 'selectors/user/abTest'
import {
  getSelectedFacets as getAlgoliaSelectedFacets,
  getSharedFacetNames as getAlgoliaSharedFacetNames,
} from 'selectors/algolia2/data'
/* =========================================================================================================================================== */

import { getFirstFacetValue } from 'components/SA/SearchProvider/helpers/url'
import { createSelector } from 'reselect'
import { getPageData, getPageParams } from 'selectors/page'
import { SAArtwork } from 'types/Artwork'
import {
  SAFacets,
  SASelectedFacets,
  SASlugifyDictionary,
  SearchProviderPagination,
  SearchProviderRedux,
} from 'types/SearchProvider'
import { getSortKeyByValue } from 'components/SA/SearchProvider/helpers'
import { getUserPreferencesDestinationZone } from 'selectors/user/cookie'
import { getSharedIsConstructorExperience } from './shared/constructorExperience'

const DEFAULT_RESULTS_PER_PAGE = 100

// Used for metadata and dataLayer
export const getSort = createSelector(
  [getPageParams],
  (pageParams): string => pageParams.sort || 'default'
)
export const getHitsPerPage = createSelector([getPageParams], (pageParams): number =>
  Number(pageParams.hitsPerPage || DEFAULT_RESULTS_PER_PAGE)
)
export const getPage = createSelector([getPageParams], (pageParams): number =>
  Number(pageParams.page || 1)
)
export const getSearchQuery = createSelector([getPageParams], (pageParams): string =>
  decodeURI(pageParams.query || '')
)
/* =========================================================================================================================================== */

// Functions to get generic search provider reducer values
export const getSearchProvider = (state: Record<string, any>): SearchProviderRedux =>
  state.searchProvider || {}

export const getSearchProviderArtworks = createSelector(
  [getSearchProvider],
  (searchProvider): ReadonlyArray<SAArtwork> => searchProvider.results || []
)

export const getSearchProviderFacets = createSelector(
  [getSearchProvider],
  (searchProvider): SAFacets => searchProvider.facets || {}
)

export const getIsPrintsPage = createSelector([getSearchProvider], (searchProvider): boolean =>
  Boolean(searchProvider.isPrintsPage)
)

// Uncomment once AB Test in Browse is removed
// export const getSelectedFacets = createSelector(
//   [getSearchProvider],
//   (searchProvider): SASelectedFacets =>
//     searchProvider.selectedFacets || {}
// )
// export const getSharedFacetNames = createSelector(
//   [getSearchProvider],
//   (searchProvider): SASelectedFacets => searchProvider.sharedFacetNames || {}
// )
/* =========================================================================================================================================== */

// Remove once AB Test in Browse is removed
export const isConstructorABTest = createSelector(
  [getSharedIsConstructorExperience],
  (isConstructorExperience): boolean => isConstructorExperience
)

export const getSelectedFacets = createSelector(
  [getSearchProvider, isConstructorABTest, getAlgoliaSelectedFacets],
  (searchProvider, isConstructorAbTestValue, algoliaSelectedFacets): SASelectedFacets =>
    isConstructorAbTestValue ? searchProvider.selectedFacets : algoliaSelectedFacets
)

export const getSharedFacetNames = createSelector(
  [getSearchProvider, isConstructorABTest, getAlgoliaSharedFacetNames],
  (searchProvider, isConstructorAbTestValue, algoliaSharedFacetNames): SASelectedFacets =>
    isConstructorAbTestValue ? searchProvider.sharedFacetNames : algoliaSharedFacetNames
)
/* =========================================================================================================================================== */

export const getSelectedCategoryFacet = createSelector(
  [getSelectedFacets],
  (selectedFacets): string => {
    const categorySelected = selectedFacets?.category?.[0]
    return categorySelected || 'All'
  }
)
export const getSlugifyDictionary = createSelector(
  [getSearchProvider],
  (searchProvider): SASlugifyDictionary => searchProvider.slugifyDictionary || {}
)

export const getPagination = createSelector(
  [getSearchProvider],
  (searchProvider): SearchProviderPagination => searchProvider.pagination || {}
)

export const getPaginationResultsPerPage = createSelector(
  [getPagination],
  (pagination): number => pagination.resultsPerPage || DEFAULT_RESULTS_PER_PAGE
)

export const getPaginationPageNumber = createSelector(
  [getPagination],
  (pagination): number => pagination.pageNumber || 1
)

export const getTotalResults = createSelector(
  [getPagination],
  (pagination): number => pagination.totalResults || 0
)

export const getSearchProviderSort = createSelector(
  [getSearchProvider, getUserPreferencesDestinationZone],
  (searchProvider, destinationZone): string =>
    getSortKeyByValue(searchProvider?.sort, destinationZone) || ''
)

export const getSearchProviderQuery = createSelector([getSearchQuery], (query): string => query)

/* =========================================================================================================================================== */

// List of facets that can be path param
export const getPathParams = createSelector(
  getPageData,
  (pageData): ReadonlyArray<string> => {
    return pageData.pathParams || []
  }
)
/* =========================================================================================================================================== */

export const getSearchProviderBasicQueryParam = createSelector(
  [getSearchProviderSort, getPaginationResultsPerPage, getPaginationPageNumber, getSearchQuery],
  (sort, hitsPerPage, page, query): Record<string, ReadonlyArray<string>> => ({
    ...(sort !== 'default'
      ? {
          sort: [`${sort}`],
        }
      : {}),
    ...(hitsPerPage !== DEFAULT_RESULTS_PER_PAGE
      ? {
          hitsPerPage: [`${hitsPerPage}`],
        }
      : {}),
    ...(page !== 0
      ? {
          page: [`${page + 1}`],
        }
      : {}),
    ...(query
      ? {
          query: [`${query}`],
        }
      : {}),
  })
)

/* =========================================================================================================================================== */

// Facets helper
export const getSelectedFacetNameAttributes = (facetName: string) =>
  createSelector(
    [getSelectedFacets],
    (selectedFacets): ReadonlyArray<string> => {
      return selectedFacets[facetName] || []
    }
  )

// Used to Know whether a Facet should be added or removed
export const doesSelectedFacetNameAttributeExist = (facetName: string, attributeValue: string) =>
  createSelector(
    [getSelectedFacetNameAttributes(facetName)],
    (facetAttributes): boolean => facetAttributes.includes(attributeValue) || false
  )

export const getSelectedFacetNameFirstAttribute = (facetName: string) =>
  createSelector(
    [getSelectedFacetNameAttributes(facetName), getSharedFacetNames],
    (facetAttributes, sharedFacetNames): string =>
      getFirstFacetValue({
        facetName,
        facetValues: facetAttributes,
        sharedFacetNames,
      })
  )
/* =========================================================================================================================================== */
